import type { ReactNode } from 'react';

import { useLocation, useNavigate, useRouteLoaderData } from '@remix-run/react';
import type { TextLeafProps } from '@shared/dream-components';
import { SerializeTextLeaf } from '@shared/dream-components';

import useGlobalModals from '~/components/GlobalModals/hooks/useGlobalModals';
import { usePageContext } from '~/context/usePageContext';
import type { RootLoaderData } from '~/root';

import { isSameSiteHref } from './isSameSiteHref';

interface Props {
  node: TextLeafProps['leaf'];
  children: ReactNode | ReactNode[];
}

const Text = ({ node }: Props) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { handleOpenModal, handleCloseModal } = useGlobalModals();
  const { subscription, setCloseModalActionDispatched, openPageModalType } = usePageContext();
  const { currentUrl } = (useRouteLoaderData('root') as RootLoaderData | undefined) || {};

  const hasMarks = node?.marks && node?.marks?.length > 0;

  if (hasMarks) {
    const linkMarks = node?.marks?.find((mark) => mark.type === 'link');

    if (linkMarks) {
      const onClick = linkMarks?.attrs && 'onClick' in linkMarks.attrs ? linkMarks.attrs.onClick : undefined;
      const action = linkMarks?.attrs && 'action' in linkMarks.attrs && linkMarks.attrs.action;
      const currentHref = linkMarks.attrs?.href || '';
      const isOpenAction = onClick === 'action' || (onClick === undefined && !!action);
      const isInternalLink =
        linkMarks.attrs?.target === '_self' && (isOpenAction || isSameSiteHref(currentHref, currentUrl));
      let url: URL | undefined;
      let handleClientSideNavigation: (() => void) | undefined;
      let isClientSideNavigation = false;

      const isTelLink = currentHref.startsWith('tel:');

      try {
        if (!isTelLink) {
          url = new URL(currentHref);
        }
      } catch {
        // Fail silently
      }

      // On actions we only want to
      if (isOpenAction) {
        // The server needs a URL too, or it renders <a href="#"> where the client renders <button>
        const pageHref = typeof window !== 'undefined' ? window.location.href : currentUrl;
        if (pageHref) {
          url = new URL(pageHref);
        }

        if (action === 'login' && url) {
          url.searchParams.set('modal', 'login');
        } else if (action === 'sign_up' && url) {
          url.searchParams.set('modal', 'signup');
        } else if (action === 'profile' && url) {
          url.pathname = `subscribe/${subscription?.subscriber_id || ''}/manage`;
        } else if (action === 'search' && url) {
          url.searchParams.set('modal', 'search');
        } else if (action === 'close_modal' && url) {
          url.searchParams.delete('modal');
        }
      }
      // Ensure the button is not open in a new tab if it has an action
      if (linkMarks.attrs && isOpenAction && url) {
        try {
          linkMarks.attrs.target = '_self';
        } catch {
          // Fail silently
        }
      }

      if (linkMarks.attrs?.target === '_self' && url && isInternalLink && !isTelLink) {
        handleClientSideNavigation = () => {
          if (typeof action === 'string' && ['sign_up', 'search', 'login', 'close_modal'].includes(action)) {
            switch (action) {
              case 'search':
              case 'login':
                handleOpenModal(action, { preventScrollReset: true });
                break;
              case 'sign_up':
                handleOpenModal('signup', { preventScrollReset: true });
                break;
              case 'close_modal':
                handleCloseModal({ preventScrollReset: true });
                if (openPageModalType) {
                  setCloseModalActionDispatched(true);
                }
                break;
              default:
                navigate(`${url.pathname}${url.search}${url.hash}`);
            }

            return;
          }
          navigate(`${url.pathname}${url.search}${url.hash}`);
        };
        isClientSideNavigation = true;
      }

      // Preserving original behavior for internal links
      if (isInternalLink && currentHref && !isOpenAction) {
        try {
          const url = new URL(currentHref);
          const searchParams = new URLSearchParams(url.search);
          let slug = `${url.pathname}${url.hash}`;

          if (slug.includes('/login') && location.pathname.startsWith('/p/')) {
            searchParams.set('postSlug', location.pathname);
            slug = `${url.pathname}?${searchParams.toString()}${url.hash}`;
            isClientSideNavigation = true;
          }

          const onClick = () => {
            navigate(slug);
          };

          return <SerializeTextLeaf leaf={node} isClientSideNavigation={isClientSideNavigation} onClick={onClick} />;
        } catch {
          // If this fails still render the node anyways
          return <SerializeTextLeaf leaf={node} />;
        }
      }

      return (
        <SerializeTextLeaf
          leaf={node}
          isClientSideNavigation={isClientSideNavigation}
          onClick={handleClientSideNavigation}
        />
      );
    }
  }

  return <SerializeTextLeaf leaf={node} />;
};

export default Text;
